import React, {memo, useMemo} from "react"
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    useReactTable,
    getCoreRowModel,
} from '@tanstack/react-table'

import DataTable from "../../../components/DataTable";

import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Icon from "@mui/material/Icon";

import Filters from "./Filters";
import Tooltip from "@mui/material/Tooltip";
import {format, parseISO} from "date-fns";


const IncidentsTable = (props) => {
    const {
        rows,
        page_count = -1,
        pagination,
        setPagination,
        onGlobalFilterChange,
        onFilterChange,
        sorting,
        onSortingChange,
        multipleEditing,
        loading,
        filterProps,
        incidentRegistry,
        incidentSettings,
        map
    } = props;
    const {t} = useTranslation(["incidents"]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'date',  // this is the key used in filter and sorting
                cell: props => format(parseISO(props.getValue()), "dd/MM/yyyy"),
                header: () => <span>{t("table.date")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "left",
                    width: "5rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'provinceAbbreviation',  // this is the key used in filter and sorting
                cell: props => props.getValue(),
                header: () => <span>{t("table.provinceId")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "left",
                    width: "5rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'locationName',  // this is the key used in filter and sorting
                cell: props => props.getValue(),
                header: () => <span>{t("table.locationName")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "left",
                    width: "5rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'incidentCategory',
                cell: props => <IncidentCategory cell={props}/>,
                header: () => <span>{t("table.incidentCategory")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "4rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'incidentSubcategory',
                cell: props => <IncidentSubcategory cell={props}/>,
                header: () => <span>{t("table.incidentSubcategory")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "4rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'numberOfPeopleInvolved',
                cell: props => props.getValue(),
                header: () => <span>{t("table.numberOfPeopleInvolved")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "2rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'numberOfPeopleCaught',
                cell: props => props.getValue(),
                header: () => <span>{t("table.numberOfPeopleCaught")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "2rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'numberOfUninjured',
                cell: props => props.getValue(),
                header: () => <span>{t("table.numberOfUninjured")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "2rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'numberOfInjured',
                cell: props => props.getValue(),
                header: () => <span>{t("table.numberOfInjured")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "2rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'numberOfDeaths',
                cell: props => props.getValue(),
                header: () => <span>{t("table.numberOfDeaths")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "2rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'notes',
                cell: props => <NoteCell cell={props}/>,
                header: () => <span>{t("table.notes")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "left",
                    width: "5rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'Actions',
                cell: props => <ActionCol cell={props}/>,
                header: () => <span>{t("table.actions")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "right",
                    width: "8rem",
                    px: 0,
                    vAlign: "top"
                }
            },
        ],
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    // Re-render happen every time the data/rows change.
    // We re-create a new array because we want to control when the table is re-rendered,
    // and it should be re-rendered when either rows change or multipleEditing changes
    const data = useMemo(() => rows ? [...rows] : [], [rows, multipleEditing])

    const table = useReactTable({
        data: data,
        columns,
        pageCount: page_count ?? -1,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onGlobalFilterChange: onGlobalFilterChange,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,  // rows are expected to be paginated (controlled mode)
        manualSorting: true,  // rows are expected to be sorted (controlled mode)
        // getPaginationRowModel: getPaginationRowModel(), // If only doing manual pagination, you don't need this
        debugTable: true,
        meta: {
            incidentRegistry: incidentRegistry,
            map
        }
    })
    return <DataTable dataTableId={props.dataTableId}
                      table={table}
                      canSearch
                      showRowColoring
                      rowColoring={props.rowColoring}
                      setRowColoring={props.setRowColoring}
                      stickyHeader={true}
                      filtersEl={<Filters onChange={onFilterChange}
                                          loading={loading}
                                          incidentRegistry={incidentRegistry}
                                          incidentSettings={incidentSettings}
                                          {...filterProps}/>}/>
}

const areEqualCellValues = (prevProps, nextProps) => {
    const prevValue = prevProps.cell.getValue();
    const nextValue = nextProps.cell.getValue();
    return prevValue === nextValue;
}

const IncidentCategory = memo(({cell}) => {
    const value = cell.getValue();
    const incidentRegistry = cell.table.options.meta.incidentRegistry;
    return incidentRegistry?.incidentCategoryV2.find(cat => cat.code === value)?.description;
}, areEqualCellValues)

const IncidentSubcategory = memo(({cell}) => {
    const value = cell.getValue();
    const incidentRegistry = cell.table.options.meta.incidentRegistry;
    return incidentRegistry?.incidentSubcategory.find(cat => cat.code === value)?.description;
}, areEqualCellValues)

const NoteCell = memo(({cell}) => {
    const value = cell.getValue();
    if (value !== "" && value != null) {
        return <Tooltip title={value} placement="bottom" disableInteractive>
            <span>{value.length > 16 ? value.substring(0, 15) + "..." : value}</span>
        </Tooltip>
    } else {
        return null;
    }
}, areEqualCellValues)

const areEqualActionCol = (prevProps, nextProps) => {
    return prevProps.cell.row.original.id === nextProps.cell.row.original.id;
}

const ActionCol = memo(({cell}) => {
    const navigate = useNavigate();
    const incident = cell.row.original;
    const lat = incident?.geom?.coordinates?.[1] ?? null;
    const lng = incident?.geom?.coordinates?.[0] ?? null;
    const {id, type} = incident;
    const map = cell.table.options?.meta.map;
    const incidentEditPath = type === "incident" ? `/incidents/${id}/edit` : `/incidents/pre2021/${id}/edit`;

    return <Stack direction="row" spacing={0.5} justifyContent="end">
        <IconButton onClick={() => map.flyTo({lat, lng})} sx={{p: 0}}>
            <Icon fontSize="small">location_searching</Icon>
        </IconButton>
        <IconButton onClick={() => navigate(incidentEditPath)} sx={{p: 0}}>
            <Icon fontSize="small">edit</Icon>
        </IconButton>
    </Stack>
}, areEqualActionCol)

export default IncidentsTable;